import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DEFAULT_CUSTOMER, money } from './rateEngine';
import { buildInvoiceDraft, currentInvoice, inPeriod, invoiceOutOfDate, invoiceTitle, manifestDay } from './payroll';
import { downloadGrossPayCsv, downloadInvoice, downloadPayrollSheet } from './pdf';
import { EmptyState, PeriodPicker, StatusBadge } from './shared';

const InvoicePanel = ({ customer, draft, issued, history, unposted, cfg, onCreate, onReissue }) => {
  const [busy, setBusy] = useState(false);
  const inv = issued || draft;
  const stale = invoiceOutOfDate(issued, draft);

  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">{customer}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              {issued ? (
                <>
                  <span className="font-mono">{invoiceTitle(issued)}</span> issued {new Date(issued.issuedAt).toLocaleDateString('en-CA')}
                </>
              ) : (
                'Draft — not issued'
              )}
              {unposted > 0 && <span className="text-amber-500 ml-2">· {unposted} unposted manifest(s) not included</span>}
              {stale && <span className="text-red-500 ml-2">· back-dated manifests posted since issue — re-issue required</span>}
            </p>
          </div>
          <div className="flex gap-2">
            {issued ? (
              <>
                <StatusBadge status="issued" label={issued.revision ? 'Final Invoice' : 'Issued'} />
                {stale && (
                  <Button id={`invoice-reissue-${customer}`} size="sm" disabled={busy} onClick={() => act(() => onReissue(issued))} className="bg-sky-600 hover:bg-sky-700 text-white">
                    <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-redo'} mr-2`}></i>Issue Final Invoice
                  </Button>
                )}
                <Button id={`invoice-download-${customer}`} size="sm" variant="outline" onClick={() => downloadInvoice(issued, cfg)}>
                  <i className="fas fa-file-pdf mr-2"></i>Download PDF
                </Button>
              </>
            ) : (
              <Button id={`invoice-issue-${customer}`} size="sm" disabled={busy || draft.lines.length === 0} onClick={() => act(() => onCreate(customer))} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <i className={`fas ${busy ? 'fa-spinner fa-spin' : 'fa-file-invoice-dollar'} mr-2`}></i>Issue invoice
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/80 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Route</th>
              <th className="text-right p-3">Pieces</th>
              <th className="text-right p-3">Delivery</th>
              <th className="text-right p-3">Accessorials</th>
              <th className="text-right p-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.manifestId} className="border-t border-border">
                <td className="p-3">{l.date}</td>
                <td className="p-3 font-mono">{l.route}</td>
                <td className="p-3 text-right">{l.pieces}</td>
                <td className="p-3 text-right">{money(l.base)}</td>
                <td className="p-3 text-right">{money(l.accessorial)}</td>
                <td className="p-3 text-right font-semibold">{money(l.amount)}</td>
              </tr>
            ))}
            {inv.lines.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-muted-foreground">No posted manifests this week.</td>
              </tr>
            )}
          </tbody>
          <tfoot className="text-sm">
            <tr className="border-t border-border">
              <td colSpan={5} className="p-2 pr-3 text-right text-muted-foreground">Subtotal</td>
              <td className="p-2 pr-3 text-right">{money(inv.subtotal)}</td>
            </tr>
            {inv.taxes.map((t) => (
              <tr key={t.code}>
                <td colSpan={5} className="p-2 pr-3 text-right text-muted-foreground">{t.label || t.code}</td>
                <td className="p-2 pr-3 text-right">{money(t.amount)}</td>
              </tr>
            ))}
            <tr className="border-t border-border font-bold">
              <td colSpan={5} className="p-3 text-right">Total</td>
              <td className="p-3 text-right text-emerald-500">{money(inv.total)}</td>
            </tr>
          </tfoot>
        </table>
        {history.length > 0 && (
          <div className="border-t border-border p-3 text-xs text-muted-foreground space-y-1">
            {history.map((h) => (
              <div key={h.id} className="flex justify-between">
                <span>
                  <StatusBadge status="superseded" label={`${h.revision ? `Final rev ${h.revision}` : 'Original'} · superseded`} className="text-[10px] mr-2" />
                  issued {new Date(h.issuedAt).toLocaleDateString('en-CA')} · {money(h.total)}
                </span>
                <button className="hover:text-foreground" onClick={() => downloadInvoice(h, cfg)}>
                  <i className="fas fa-file-pdf mr-1"></i>PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Screen 5 — Payroll & Invoicing
 * Bi-weekly driver payroll sheet (payroll_sheet.pdf, no GST/QST) and weekly customer invoices (GST + QST)
 * from posted manifests. A back-dated manifest posted into an invoiced week triggers a revised invoice.
 */
const PayrollInvoicing = ({ payPeriod, invoicePeriod, manifests, cards, settlements, invoices, onCreateInvoice, onReissueInvoice }) => {
  const weekManifests = useMemo(
    () => manifests.filter((m) => inPeriod(manifestDay(m), invoicePeriod.start, invoicePeriod.cfg)),
    [manifests, invoicePeriod.start, invoicePeriod.cfg]
  );
  const customers = useMemo(() => [...new Set(weekManifests.map((m) => m.customer || DEFAULT_CUSTOMER))].sort(), [weekManifests]);
  const drafts = settlements.filter((s) => s.status === 'draft').length;

  return (
    <div className="space-y-6" id="payroll-invoicing-screen">
      <div>
        <h2 className="text-xl font-bold">Payroll &amp; Invoicing</h2>
        <p className="text-sm text-muted-foreground">Export the bi-weekly payroll sheet and issue weekly customer invoices from posted manifests.</p>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base">Gross Pay Sheet</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Gross pay per each driver's rate card, for the accountant to finalize payroll. Vacation pay is accrued, not paid out.
                {drafts > 0 && <span className="text-amber-500 ml-1">{drafts} settlement(s) still in draft — approve them on Driver Settlements.</span>}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <PeriodPicker {...payPeriod} idPrefix="payroll" title="Pay period" />
              <Button id="btn-download-payroll" size="sm" variant="outline" disabled={settlements.length === 0} onClick={() => downloadPayrollSheet(settlements, payPeriod.start, payPeriod.cfg)}>
                <i className="fas fa-file-pdf mr-2"></i>PDF
              </Button>
              <Button id="btn-download-payroll-csv" size="sm" variant="outline" disabled={settlements.length === 0} onClick={() => downloadGrossPayCsv(settlements, payPeriod.start, payPeriod.cfg)}>
                <i className="fas fa-file-csv mr-2"></i>CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/80 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left p-3">Driver</th>
                <th className="text-right p-3">Pieces</th>
                <th className="text-right p-3">Manifest pay</th>
                <th className="text-right p-3">Adjustments</th>
                <th className="text-right p-3">Gross pay</th>
                <th className="text-right p-3">Vacation accrued</th>
                <th className="text-left p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {settlements.map((s) => (
                <tr key={s.id} className="border-t border-border">
                  <td className="p-3">
                    {s.driver.name} <span className="text-xs text-muted-foreground ml-1">{s.driver.id}</span>
                  </td>
                  <td className="p-3 text-right">{s.pieces}</td>
                  <td className="p-3 text-right">{money(s.gross)}</td>
                  <td className="p-3 text-right">{money(s.adjTotal)}</td>
                  <td className="p-3 text-right font-semibold">{money(s.grossPay)}</td>
                  <td className="p-3 text-right text-muted-foreground">{s.vacationRate ? money(s.vacationAccrued) : '—'}</td>
                  <td className="p-3">
                    <StatusBadge status={s.status} />
                  </td>
                </tr>
              ))}
              {settlements.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-muted-foreground">No driver settlements this pay period.</td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <h3 className="text-base font-semibold">Customer Invoices</h3>
        <PeriodPicker {...invoicePeriod} idPrefix="invoices" title="Week" />
      </div>
      {customers.length === 0 ? (
        <EmptyState icon="fa-file-invoice" title="No manifests this week" />
      ) : (
        customers.map((c) => (
          <InvoicePanel
            key={c}
            customer={c}
            cfg={invoicePeriod.cfg}
            draft={buildInvoiceDraft(manifests, cards, invoicePeriod.start, c, invoicePeriod.cfg)}
            issued={currentInvoice(invoices, invoicePeriod.start, c)}
            history={invoices
              .filter((i) => i.periodStart === invoicePeriod.start && i.customer === c && i.status === 'superseded')
              .sort((a, b) => (b.revision || 0) - (a.revision || 0))}
            unposted={weekManifests.filter((m) => (m.customer || DEFAULT_CUSTOMER) === c && m.status !== 'posted').length}
            onCreate={onCreateInvoice}
            onReissue={onReissueInvoice}
          />
        ))
      )}
    </div>
  );
};

export default PayrollInvoicing;
